/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useEffect } from 'react'

import { EuiFormRow, EuiRadio, EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { CSP } from '@modules/ui-types/providers'

type ConnectivityType =
  | 'vpce'
  | 'azure_private_endpoint'
  | 'gcp_private_service_connect_endpoint'
  | 'remote_cluster'

type Props = {
  value: string | undefined // ideally this would be ConnectivityType, but the spec says string for now
  onChange: (value: ConnectivityType) => void
  csp: CSP | ''
}

function getPrivateLinkType(csp: CSP | ''): ConnectivityType {
  switch (csp) {
    case 'azure':
      return 'azure_private_endpoint' as const
    case 'gcp':
      return 'gcp_private_service_connect_endpoint' as const
    default:
      return 'vpce' as const
  }
}

const ConnectivityField: React.FunctionComponent<Props> = ({
  csp,
  value = getPrivateLinkType(csp),
  onChange,
}) => {
  useEffect(() => {
    if (value !== getPrivateLinkType(csp) && value !== 'remote_cluster') {
      onChange(getPrivateLinkType(csp))
    }
  }, [csp, value, onChange])

  return (
    <EuiFormRow
      label={<FormattedMessage id='connectivityField.label' defaultMessage='Connectivity' />}
    >
      <EuiFlexGroup gutterSize='l'>
        <EuiFlexItem grow={false}>
          <EuiRadio
            id='connectivity-privatelink'
            label={
              <FormattedMessage
                id='connectivityField.privatelinkLabel'
                defaultMessage='Privatelink'
              />
            }
            checked={value === getPrivateLinkType(csp)}
            onChange={() => onChange(getPrivateLinkType(csp))}
            name='connectivity'
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiRadio
            id='connectivity-remote-cluster'
            label={
              <FormattedMessage
                id='connectivityField.remoteClusterLabel'
                defaultMessage='Remote cluster'
              />
            }
            checked={value === 'remote_cluster'}
            onChange={() => onChange('remote_cluster')}
            name='connectivity'
          />
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiFormRow>
  )
}

export default ConnectivityField
